import { useMemo } from 'react';
import Sticky from 'react-stickynode';
import styled from 'styled-components';
import { border } from 'styled-system';

import { Box } from '@hivebrite/design-system/Box';
import { Card } from '@hivebrite/design-system/Card';
import { OrderSummaryCard } from '@hivebrite/design-system/OrderSummary';
import { Skeleton } from '@hivebrite/design-system/Skeleton';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { PromoCode } from 'hb-react/shared/apps/Events/pages/Booking/components/PromoCode';
import { useBooking } from 'hb-react/shared/apps/Events/pages/Booking/hooks/useBooking';
import ShoppingCard from 'hb-react/shared/components/ShoppingCard';
import type { CartItem } from 'hb-react/shared/components/ShoppingCard/hooks/v2';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

import { Card as SidebarCard } from '../Card';
import { ShoppingCardLine } from '../ShoppingCardLine/shopping-card-line.component';

export const Sidebar = ({ page }: { page: string }) => {
  const breakpoint = useBreakpoint();
  const isMobile = useMemo(() => ['s', 'm'].includes(breakpoint), [breakpoint]);

  const { flagEvPaymentsMovePromocode, flagEvOrderSummaryDsEvents } =
    useFeatureFlag();
  const { event, cart, metadata } = useBooking();
  const { currentUser, currentNetwork } = useDefaultAppContexts();

  const eligibleForPromocodes =
    flagEvPaymentsMovePromocode &&
    !['success', 'payment'].includes(page) &&
    metadata?.eligible_for_promocodes;

  const items = useMemo(() => cart?.cart_items ?? [], [cart?.cart_items]);

  // TODO: Remove once flagEvOrderSummaryDsEvents is removed
  const cartItems: (CartItem & { render: () => JSX.Element })[] = useMemo(
    () =>
      Object.values<CartItem>(
        items.reduce((items, item) => {
          if (!('line_itemable_id' in item)) {
            return items;
          }

          if (item.line_itemable_type === 'Ecommerce::PromoCode') {
            return {
              ...items,
              [item.id]: {
                ...item,
                quantity: 1,
              },
            };
          }

          const compoundKey = `${item.line_itemable_id}-${item.line_itemable_type}`;

          return {
            ...items,
            [compoundKey]: {
              ...item,
              quantity: item.quantity + (items[compoundKey]?.quantity || 0),
            },
          };
        }, {}),
      )
        .map((item) => ({
          ...item,
          render: () => <ShoppingCardLine item={item} />,
        }))
        .reverse(),
    [items],
  );

  return (
    <Sticky
      enabled={!isMobile}
      top="header"
      bottomBoundary="#event-registration-container"
    >
      {({ status }) =>
        flagEvOrderSummaryDsEvents ? (
          <Box flexDirection="column" data-testid="event-shopping-card">
            {['s', 'm'].includes(breakpoint) || page === 'payment' ? null : (
              <>{event ? <SidebarCard event={event} /> : <Skeleton />}</>
            )}

            <OrderSummaryCard
              cardProps={{
                mt: page === 'payment' ? 0 : 4,
                px: 4,
                py: 6,
                mb: { _: 0, l: 80 },
              }}
              items={items}
              locale={
                currentUser?.locale || I18n.locale || currentNetwork.locales[0]
              }
              isCompact={['s', 'm'].includes(breakpoint)}
            />

            {eligibleForPromocodes && (
              <Card p={4} mt={4}>
                <PromoCode />
              </Card>
            )}
          </Box>
        ) : (
          <Box flexDirection="column">
            <AsideWrapper p={0} $isSticked={status === Sticky.STATUS_FIXED}>
              <ShadowedWrapper $withShoppingCard>
                {event ? <SidebarCard event={event} /> : <Skeleton />}
              </ShadowedWrapper>
              <Box display="block" data-testid="event-shopping-card">
                <ShoppingCard items={cartItems} displayInModal={isMobile} />
              </Box>
            </AsideWrapper>

            {eligibleForPromocodes && (
              <Card p={4} mt={4}>
                <PromoCode />
              </Card>
            )}
          </Box>
        )
      }
    </Sticky>
  );
};

const ShadowedWrapper = styled(Box).attrs<{ $withShoppingCard: boolean }>(
  ({ theme, $withShoppingCard }) => ({
    display: 'block',
    borderTopLeftRadius: theme.themeVariables.globalBorderRadius,
    borderTopRightRadius: theme.themeVariables.globalBorderRadius,
    borderBottomLeftRadius: $withShoppingCard
      ? { _: theme.themeVariables.globalBorderRadius, l: 0 }
      : theme.themeVariables.globalBorderRadius,
    borderBottomRightRadius: $withShoppingCard
      ? { _: theme.themeVariables.globalBorderRadius, l: 0 }
      : theme.themeVariables.globalBorderRadius,
  }),
)<{ $withShoppingCard: boolean }>`
  ${border}
  width: 100%;
`;

const AsideWrapper = styled(Card)<{ $isSticked: boolean }>`
  transform: translateY(${(props) => (props.$isSticked ? -25 : 0)}px);
  transition: transform 0.2s;
`;
